import * as React from 'react'
import {
  Box,
  Typography,
  Popover,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  MenuItem,
} from '@mui/material'
import MuteBellIcon from '@assets/ConsumersNotifications/MuteBell.svg'
import UnMuteBellIcon from '@assets/ConsumersNotifications/UnMute.svg'
import AppDatePicker from '@/components/DatePickers/AppDatePicker'
import {
  formatDay,
  parseDay,
  startOfDay,
} from '@/components/DatePickers/dateRange'
import { useTableStore } from '@/store/table/table'
import type { AlertType } from '@/services/notificationService'
import { useAppTheme } from '@/theme/ThemeContext'

export type AlertsManageValues = {
  mode: 'enabled' | 'muted'
  until: string
  alertType: string
  // New fields for the two-section layout
  messagesMode: 'enabled' | 'muted'
  messagesUntil: string
  messagesAlertType: string
  alertsMode: 'enabled' | 'muted'
  alertsUntil: string
  alertsAlertType: string
}

type AlertsManagePopoverProps = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  values: AlertsManageValues
  onChange: (v: AlertsManageValues) => void
  onSave: (v: AlertsManageValues) => void
  /** The site's alert types for the type pickers; without them the pickers offer only "all". */
  alertTypes?: AlertType[]
  /**
   * 'meterPage' sits the popover's left edge on the bell's, widens it to 445px,
   * squares the fields, and calls the second section "התראות מבוטלות".
   */
  variant?: 'default' | 'meterPage'
}

type AlertTypeOption = { value: string; label: string }

const AlertsManagePopover: React.FC<AlertsManagePopoverProps> = ({
  anchorEl,
  open,
  onClose,
  values,
  onChange,
  onSave,
  alertTypes: siteAlertTypes,
  variant = 'default',
}) => {
  const meterPage = variant === 'meterPage'
  const { mode: themeMode } = useAppTheme()
  const isDarkMode = themeMode === 'dark'

  const colors = {
    text: isDarkMode ? '#fff' : '#003F61',
    subText: isDarkMode ? '#93A3B5' : '#64748B',
    bg: isDarkMode ? '#162645' : '#fff',
    border: isDarkMode ? '#445876' : '#E4E7EB',
    confirmBtn: isDarkMode ? '#81B5FF' : '#72A4EB',
    saveBtnBg: isDarkMode ? '#202B40' : '#E3F5FF',
    saveBtnBorder: isDarkMode ? '#445876' : '#E4E7EB',
    menuItemHover: isDarkMode ? '#202B40' : '#E3F5FF',
    sectionBorder: isDarkMode ? '#2A364F' : '#E2E8F0',
    fieldBorder: isDarkMode ? '#93A3B5' : '#003F61',
  }

  const { rowsStore } = useTableStore()
  const id = open ? 'alerts-manage-popover' : undefined

  const alertTypes = React.useMemo<AlertTypeOption[]>(
    () =>
      siteAlertTypes
        ? siteAlertTypes.map(type => ({
            value: String(type.alertTypeId),
            label: type.alertTypeName,
          }))
        : Array.from(
            new Set(rowsStore.map((row: any) => row.notification).filter(Boolean))
          ).map(name => ({ value: String(name), label: String(name) })),
    [siteAlertTypes, rowsStore]
  )
  // A saved type the list does not hold (yet) reads as "all" instead of an empty field.
  const shownAlertType = (value: string) =>
    alertTypes.some(type => type.value === value) ? value : 'all'

  /* ── Shared text field styles ── */
  const fieldSx = {
    '& .MuiInputLabel-root': { 
      color: colors.text, 
      fontFamily: 'Rubik', 
      fontSize: '13px',
      right: 12,
      left: 'auto',
      transformOrigin: 'right',
    },
    '& .MuiOutlinedInput-root': {
      color: colors.text,
      fontFamily: 'Rubik',
      fontSize: '13px',
      '& fieldset': { 
        borderColor: colors.border,
        textAlign: 'right',
      },
      '& legend': {
        textAlign: 'right',
        marginLeft: 'auto',
      },
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(0, -9px) scale(0.75) !important',
      right: 12,
    },
    '& .MuiSvgIcon-root': { color: colors.text },
    ...(meterPage && {
      '& .MuiInputLabel-root': {
        color: colors.text,
        fontFamily: 'Rubik',
        fontSize: '14px',
        right: 12,
        left: 'auto',
        transformOrigin: 'right',
      },
      '& .MuiOutlinedInput-root': {
        color: colors.text,
        fontFamily: 'Rubik',
        fontSize: '16px',
        borderRadius: 0,
        '& fieldset': { borderColor: colors.fieldBorder, textAlign: 'right' },
        '& legend': { textAlign: 'right', marginLeft: 'auto' },
        '&.Mui-disabled': { opacity: 0.5 },
        '& .Mui-disabled': { WebkitTextFillColor: colors.text },
      },
    }),
  }

  /* ── Radio label helper ── */
  const radioLabel = (icon: string, text: string, alt: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <img src={icon} alt={alt} style={{ width: meterPage ? 24 : 18, height: meterPage ? 24 : 18, filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }} />
      <Typography sx={{ color: colors.text, fontFamily: 'Rubik', fontSize: meterPage ? '16px' : '14px', fontWeight: 300 }}>{text}</Typography>
    </Box>
  )

  /* ── Controls row (dropdown + date) ── */
  const renderControlsRow = ({
    alertTypeValue,
    onAlertTypeChange,
    until,
    onUntilChange,
    disabled,
  }: {
    alertTypeValue: string
    onAlertTypeChange: (v: string) => void
    until: string
    onUntilChange: (v: string) => void
    disabled: boolean
  }) => (
    <Box sx={{ display: 'flex', flexDirection: 'row-reverse', gap: 1.5, mt: 1.5 }}>
      <Box sx={{ flex: 1 }}>
        <TextField
          disabled={disabled}
          label="סוג התראה"
          size="small"
          select
          fullWidth
          dir="rtl"
          value={shownAlertType(alertTypeValue)}
          onChange={e => onAlertTypeChange(e.target.value)}
          sx={fieldSx}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  bgcolor: colors.bg,
                  '& .MuiMenuItem-root': {
                    color: colors.text,
                    fontFamily: 'Rubik',
                    fontSize: '13px',
                    '&:hover': { bgcolor: colors.menuItemHover },
                    '&.Mui-selected': { bgcolor: colors.menuItemHover },
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="all" sx={{ display: 'flex', justifyContent: 'flex-end' }}>כל ההתראות</MenuItem>
          {alertTypes.map(type => (
            <MenuItem key={type.value} value={type.value} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ flex: 1 }}>
        <AppDatePicker
          label="עד תאריך"
          value={parseDay(until)}
          onChange={next => onUntilChange(formatDay(next))}
          minDate={startOfDay(new Date())}
          disabled={disabled}
          clearable
          width="100%"
        />
      </Box>
    </Box>
  )

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: meterPage ? 'left' : 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: meterPage ? 'left' : 'right' }}
      PaperProps={{
        sx: {
          p: 3,
          borderRadius: meterPage ? '8px' : 3,
          width: meterPage ? 445 : 380,
          maxWidth: '90vw',
          bgcolor: colors.bg,
        },
      }}
    >
      {/* Title */}
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: meterPage ? 400 : 700,
          fontSize: meterPage ? '18px' : undefined,
          color: colors.text,
          mb: meterPage ? 1.5 : 3,
          direction: 'rtl',
          fontFamily: 'Rubik, sans-serif',
          textAlign: meterPage ? 'right' : 'center',
        }}
      >
        ניהול הודעות והתראות לצרכן
      </Typography>
      {meterPage && <Box sx={{ borderBottom: `1px solid ${colors.sectionBorder}`, mb: 2 }} />}

      {/* ═══════ Section 1: הודעות (Messages) ═══════ */}
      <Box sx={{ mb: 2.5 }}>
        <RadioGroup
          value={values.messagesMode}
          onChange={e => onChange({ ...values, messagesMode: e.target.value as 'enabled' | 'muted' })}
          dir="rtl"
        >
          <FormControlLabel
            value="enabled"
            control={<Radio size={meterPage ? 'medium' : 'small'} sx={{ color: colors.text, '&.Mui-checked': { color: colors.confirmBtn } }} />}
            label={radioLabel(UnMuteBellIcon, 'הודעות מופעלות', 'Active Messages')}
          />
          <FormControlLabel
            value="muted"
            control={<Radio size={meterPage ? 'medium' : 'small'} sx={{ color: colors.text, '&.Mui-checked': { color: colors.confirmBtn } }} />}
            label={radioLabel(MuteBellIcon, 'הודעות מושתקות', 'Muted Messages')}
          />
        </RadioGroup>

        {renderControlsRow({
          alertTypeValue: values.messagesAlertType,
          onAlertTypeChange: v => onChange({ ...values, messagesAlertType: v }),
          until: values.messagesUntil,
          onUntilChange: v => onChange({ ...values, messagesUntil: v }),
          disabled: values.messagesMode === 'enabled',
        })}
      </Box>

      {/* Divider between sections */}
      <Box sx={{ borderBottom: `1px solid ${colors.sectionBorder}`, mb: 2.5 }} />

      {/* ═══════ Section 2: התראות (Alerts) ═══════ */}
      <Box sx={{ mb: 2.5 }}>
        <RadioGroup
          value={values.alertsMode}
          onChange={e => onChange({ ...values, alertsMode: e.target.value as 'enabled' | 'muted' })}
          dir="rtl"
        >
          <FormControlLabel
            value="enabled"
            control={<Radio size={meterPage ? 'medium' : 'small'} sx={{ color: colors.text, '&.Mui-checked': { color: colors.confirmBtn } }} />}
            label={radioLabel(UnMuteBellIcon, 'התראות מופעלות', 'Active Alerts')}
          />
          <FormControlLabel
            value="muted"
            control={<Radio size={meterPage ? 'medium' : 'small'} sx={{ color: colors.text, '&.Mui-checked': { color: colors.confirmBtn } }} />}
            label={radioLabel(MuteBellIcon, meterPage ? 'התראות מבוטלות' : 'התראות מושתקות', 'Muted Alerts')}
          />
        </RadioGroup>

        {renderControlsRow({
          alertTypeValue: values.alertsAlertType,
          onAlertTypeChange: v => onChange({ ...values, alertsAlertType: v }),
          until: values.alertsUntil,
          onUntilChange: v => onChange({ ...values, alertsUntil: v }),
          disabled: values.alertsMode === 'enabled',
        })}
      </Box>

      {/* Save Button */}
      <Box
        sx={{
          display: 'flex',
          height: meterPage ? '37px' : '36px',
          padding: '4px 16px',
          justifyContent: 'center',
          alignItems: 'center',
          fontWeight: 700,
          borderRadius: '6px',
          border: `1px solid ${colors.saveBtnBorder}`,
          background: colors.saveBtnBg,
          width: meterPage ? '87px' : '80px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': { opacity: 0.85 },
        }}
        onClick={() => onSave(values)}
      >
        <Typography sx={{ color: colors.text, fontFamily: 'Rubik', fontSize: meterPage ? '16px' : '14px', fontWeight: meterPage ? 400 : 500 }}>שמירה</Typography>
      </Box>

    </Popover>
  )
}

export default AlertsManagePopover
